import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import type { AppControlContext } from '@overlayqa/shared'
import { HugeiconsIcon } from '@hugeicons/react'
import type { IconSvgElement } from '@hugeicons/react'
import { Refresh01Icon } from '@hugeicons/core-free-icons'

const buttonStyles = cva(
  // Radius, transition, focus trigger and press match the `.btn` base in
  // styles/index.css. Named contexts supply the app and onboarding sizes;
  // the compatibility classes consume the same generated profiles.
  // App ghost deliberately has a tighter inset.
  // btn-frame shares the native-border geometry in styles/index.css: padding
  // is the outer content inset minus the resting border, so emphasis does not
  // widen an auto-sized button or move its label/icon.
  //
  // The transition NAMES its properties rather than using `transition-all`:
  // `all` animated the button's width when a label changes mid-action
  // ("Save" -> "Saving..."). Colour and transform are the two that should move,
  // and transform has to be in the list or the press snaps instead of easing.
  //
  // `active:scale-[0.97]` is the press. Until 2026-09-13 every button system in
  // the repo transitioned colour only and declared no `:active` rule, so a push
  // produced nothing the eye can catch; `pnpm press-feedback:check` now pins it
  // across all of them. `motion-reduce:active:scale-100` parks the movement:
  // measured that day, Tailwind emits it AFTER the `:active` rule at equal
  // specificity, so the override wins.
  'btn-frame gap-[var(--btn-gap)] inline-flex items-center justify-center rounded-md font-medium transition-[color,background-color,border-color,transform] active:scale-[0.97] motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-primary-500 text-white hover:bg-primary-600',
        // Mirrors `.btn-secondary` in styles/index.css, which is
        // the shipped meaning of "secondary": a quiet white button, not a second
        // brand-tinted primary. Keep the two in step.
        secondary: 'btn-border-1 bg-white text-gray-700 border border-gray-200 hover:bg-gray-50',
        outline: 'btn-border-2 bg-white border-2 border-primary-500 text-primary-600 hover:bg-primary-50',
        ghost: 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
        link: 'text-primary-600 underline-offset-4 hover:underline',
      },
      size: {
        sm: 'control-app-onboarding-compact',
        md: 'control-app-onboarding',
        lg: 'control-app-onboarding-prominent',
        icon: 'control-app-form btn-icon-only [--btn-icon-size:1rem]',
        small: 'control-app-small rounded-[var(--oq-corner-compact)]',
        compact: 'control-app-compact',
        toolbar: 'control-app-toolbar',
        standard: 'control-app-standard',
        form: 'control-app-form',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
)

const contextSizes = {
  small: 'small', compact: 'compact', toolbar: 'toolbar', standard: 'standard', form: 'form',
  'onboarding-compact': 'sm', onboarding: 'md', 'onboarding-prominent': 'lg',
} as const satisfies Record<AppControlContext, NonNullable<VariantProps<typeof buttonStyles>['size']>>

type ButtonContextProps =
  | { context: AppControlContext; size?: never }
  | { context?: never; /** Compatibility with the existing onboarding size API. */ size?: 'sm' | 'md' | 'lg' | 'icon' | null }
type ButtonAppearance = Pick<VariantProps<typeof buttonStyles>, 'variant'> & ButtonContextProps & {
  iconOnly?: boolean
  className?: string
}

/** Shared class API for buttons and links. Existing size callers retain their footprint. */
export function buttonVariants({ context, size, variant, iconOnly, className }: ButtonAppearance = {}) {
  const resolved = context ? contextSizes[context] : size ?? 'md'
  const appGhost = context && !context.startsWith('onboarding') && variant === 'ghost'
  return cn(buttonStyles({ variant, size: resolved }), appGhost && 'btn-app-ghost text-gray-600', iconOnly && 'btn-icon-only', className)
}

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & ButtonAppearance & {
  isLoading?: boolean
  loadingLabel?: string
  icon?: IconSvgElement
  iconPosition?: 'left' | 'right'
}

/**
 * Text children ride in a span that trims its box to the cap height (`.text-cap-trim`
 * in styles/index.css), so the flex centring lands on the letters and the icon beside
 * them centres on the same line. Measured 2026-10-22: without it a 14px label sits
 * 0.78px low and its icon 0.78px high.
 */
function capTrimmed(children: React.ReactNode): React.ReactNode {
  return React.Children.map(children, (child) =>
    (typeof child === 'string' && child.trim() !== '') || typeof child === 'number'
      ? <span className="text-cap-trim">{child}</span>
      : child
  )
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, context, iconOnly, isLoading, loadingLabel = 'Loading...', icon, iconPosition = 'left', children, disabled, ...props }, ref) => {
    const glyphOnly = iconOnly || size === 'icon'
    const iconClass = 'shrink-0 h-[var(--btn-icon-size)] w-[var(--btn-icon-size)]'
    const iconElement = icon && !isLoading && (
      <HugeiconsIcon icon={icon} size={16} strokeWidth={2} color="currentColor" className={iconClass} aria-hidden="true" />
    )
    const sizing: ButtonContextProps = context ? { context } : { size }
    return (
      <button
        className={buttonVariants({ variant, ...sizing, iconOnly, className })}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
        aria-busy={isLoading || props['aria-busy'] || undefined}
      >
        {isLoading ? (
          <>
            <HugeiconsIcon icon={Refresh01Icon} size={16} strokeWidth={2} color="currentColor" className={`animate-spin ${iconClass}`} aria-hidden="true" />
            {!glyphOnly && <span className="text-cap-trim">{loadingLabel}</span>}
          </>
        ) : (
          <>
            {icon && iconPosition === 'left' && iconElement}
            {capTrimmed(children)}
            {icon && iconPosition === 'right' && iconElement}
          </>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }
